<template>
    <div class="app-toast" :class="toastConfig.className">
        <div class="app-toast__content">
            <!-- Иконка -->
            <feather-icon
                v-if="toastConfig.isFeatherIcon"
                size="16"
                :icon="toastConfig.icon"
                class="app-toast__icon flex-shrink-0"
            />
            <b-avatar v-else size="16" class="app-toast__icon flex-shrink-0">
                <img
                    width="18"
                    height="18"
                    :src="toastConfig.icon"
                    :alt="variant"
                />
            </b-avatar>

            <!-- Контент: заголовок + текст + действие -->
            <div class="app-toast__body">
                <p v-if="title" class="app-toast__title">{{ title }}</p>
                <p v-if="text" class="app-toast__message">{{ text }}</p>
                <button
                    v-if="action"
                    class="app-toast__action"
                    @click="handleAction"
                >
                    <span :style="`color: ${toastConfig.textColor}`">
                        {{ action.label }}
                    </span>
                </button>
            </div>

            <!-- Кнопка закрытия -->
            <span
                v-if="!hideClose"
                class="app-toast__close-button"
                @click="$emit('close-toast')"
            >
                <feather-icon
                    size="18"
                    icon="XIcon"
                    class="app-toast__close-icon"
                />
            </span>
        </div>
    </div>
</template>

<script>
import { computed } from '@vue/composition-api'
import { BAvatar } from 'bootstrap-vue'

import errorIcon from '@/assets/images/icons/toast/error-triangle-fill.svg'
import infoIcon from '@/assets/images/icons/toast/info-circle-fill.svg'
import successIcon from '@/assets/images/icons/toast/success-circle-fill.svg'
import warningIcon from '@/assets/images/icons/toast/warning-circle-fill.svg'

export default {
    components: { BAvatar },
    props: {
        variant: { type: String, default: 'info' },
        title: { type: String, default: null },
        text: { type: String, default: '' },
        icon: { type: String, default: null },
        action: { type: Object, default: null },
        hideClose: { type: Boolean, default: false },
    },
    emits: ['close-toast', 'action'],
    setup(props, { emit }) {
        const handleAction = () => {
            emit('action', props.action?.callback?.())
        }

        const toastConfig = computed(() => {
            const icons = {
                success: successIcon,
                warning: warningIcon,
                error: errorIcon,
                info: infoIcon,
            }
            const configs = {
                success: {
                    className: 'app-toast--success',
                    textColor: '#0a3622',
                },
                warning: {
                    className: 'app-toast--warning',
                    textColor: '#664d03',
                },
                error: { className: 'app-toast--error', textColor: '#842029' },
                info: { className: 'app-toast--info', textColor: '#343a40' },
            }
            const variant = props.variant || 'info'
            const config = configs[variant] || configs.info
            const icon = props.icon || icons[variant] || icons.info
            const isFeatherIcon = icon.endsWith('Icon')
            return { ...config, icon, isFeatherIcon }
        })

        return { toastConfig, handleAction }
    },
}
</script>

<style scoped lang="scss">
.app-toast {
    &__content {
        position: relative;
        display: flex;
        align-items: flex-start;
        gap: 10px;
        max-width: 480px;
        padding: 16px;
        border-radius: 6px;
    }

    &__icon {
        display: inline-flex;
        flex-shrink: 0;
        background-color: transparent;
        margin-top: 4px;
    }

    &__body {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex: 1;
        min-width: 0;
        padding-right: 20px;
    }

    &__title,
    &__message,
    &__action {
        font-family: 'Inter', sans-serif;
        font-size: 16px;
        line-height: 24px;
        white-space: pre-line;
        margin: 0;
    }

    &__title {
        font-weight: bold;
    }

    &__action {
        padding: 8px;
        background: none;
        cursor: pointer;
        border-radius: 4px;
        border: none;
        font: inherit;
        text-align: left;

        &:hover {
            text-decoration: underline;
        }
    }

    ::v-deep .b-avatar-custom {
        width: auto !important;
        height: auto !important;
        border-radius: 0 !important;
        overflow: visible !important;
    }

    &__close-button {
        position: absolute;
        top: 5px;
        right: 5px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: background-color 0.2s;

        &:hover {
            background-color: rgba(0, 0, 0, 0.1);
        }
    }

    &__close-icon {
        color: inherit;
        opacity: 0.7;
        transition: opacity 0.2s;

        &:hover {
            opacity: 1;
        }
    }

    &--success {
        .app-toast__content {
            background-color: #d1e7dd;
            color: #0a3622;
            border: 1px solid #75b798;
        }
        .app-toast__action {
            border: 1px solid #75b798;
            &:hover {
                background: #a3cfbb;
            }
        }
        .app-toast__close-button:hover {
            background-color: rgba(10, 54, 34, 0.1);
        }
    }

    &--info {
        .app-toast__content {
            background-color: #e9ecef;
            color: #343a40;
            border: 1px solid #ced4da;
        }
        .app-toast__action {
            border: 1px solid #ced4da;
            &:hover {
                background: #ced4da;
            }
        }
        .app-toast__close-button:hover {
            background-color: rgba(52, 58, 64, 0.1);
        }
    }

    &--warning {
        .app-toast__content {
            background-color: #fff3cd;
            color: #664d03;
            border: 1px solid #ffda6a;
        }
        .app-toast__action {
            border: 1px solid #ffda6a;
            &:hover {
                background: #ffda6a;
            }
        }
        .app-toast__close-button:hover {
            background-color: rgba(102, 77, 3, 0.1);
        }
    }

    &--error {
        .app-toast__content {
            background-color: #f8d7da;
            color: #842029;
            border: 1px solid #f5c2c7;
        }
        .app-toast__action {
            border: 1px solid #f5c2c7;
            &:hover {
                background: #f5c2c7;
            }
        }
        .app-toast__close-button:hover {
            background-color: rgba(132, 32, 41, 0.1);
        }
    }
}

@media screen and (max-width: 600px) {
    .app-toast__content {
        max-width: 100%;
        padding: 8px 16px;
    }

    .app-toast__close-button {
        top: 10px;
        right: 10px;
    }
}
</style>

<style lang="scss">
.dark-layout {
    .app-toast {
        &--success,
        &--info,
        &--warning,
        &--error {
            .app-toast__icon,
            .app-toast__icon.b-avatar {
                background-color: inherit !important;
            }
        }
    }
}
</style>
